import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { XIcon } from "../../../components/shared/Icons";
import { backdropClose } from "../../../components/shared/backdrop";

interface ApiKey {
  id: string;
  name: string;
  provider: string;
  masked: string;
  created_at: string;
}

export const Route = createFileRoute("/_authed/settings/api-keys")({
  component: ApiKeysPage,
});

// ── shared button styles ───────────────────────────────────────────────────
const btnPrimary: React.CSSProperties = {
  background: "var(--brand)", color: "#fff", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
};

const btnGhost: React.CSSProperties = {
  background: "none", color: "var(--ink-secondary)", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, border: "1px solid var(--line-strong)", cursor: "pointer",
};

// ── modal ──────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={() => onClose()} label={title} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 420, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
          <button aria-label="Fechar" onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>
        {children}
      </DialogPanel>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
};

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta", "cartesia", "groq", "analise", "tarefas"] as const;

// ── CreateApiKeyModal ──────────────────────────────────────────────────────
function CreateApiKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: "", provider: "openai", key: "" });
  const mut = useMutation({ mutationFn: () => api.post("/api-keys", form), onSuccess: onCreated });
  return (
    <Modal title="Nova chave API" onClose={onClose}>
      <FormField label="Nome">
        <input style={inputStyle} placeholder="Ex: OpenAI Production" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </FormField>
      <FormField label="Provider">
        <select style={inputStyle} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
          {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </FormField>
      <FormField label="Chave">
        <input type="password" style={inputStyle} placeholder="sk-..." value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} />
      </FormField>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} onClick={() => mut.mutate()} disabled={mut.isPending}>
          {mut.isPending ? "..." : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}

// ── ApiKeysPage ────────────────────────────────────────────────────────────
// ── DeleteConfirmModal ─────────────────────────────────────────────────────
function DeleteConfirmModal({ keyName, onClose, onConfirm }: { keyName: string; onClose: () => void; onConfirm: () => void }) {
  const [step, setStep] = useState<1 | 2>(1);

  return (
    <Modal title="Deletar chave API" onClose={onClose}>
      {step === 1 ? (
        <>
          <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
            Tem certeza que deseja deletar a chave <strong style={{ color: "var(--ink)" }}>{keyName}</strong>?
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={btnGhost} onClick={onClose}>Cancelar</button>
            <button style={{ ...btnPrimary, background: "var(--danger)" }} onClick={() => setStep(2)}>
              Sim, deletar
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ background: "var(--danger-soft)", border: "1px solid var(--danger-soft-line)", borderRadius: 8, padding: "12px 14px" }}>
            <p style={{ fontSize: 13, color: "var(--danger)", margin: 0, fontWeight: 500 }}>
              Atenção: isso vai parar todos os serviços que usam esta chave.
            </p>
            <p style={{ fontSize: 12, color: "var(--ink-secondary)", margin: "6px 0 0" }}>
              Agentes, integrações e automações que dependem desta chave vão falhar imediatamente após a exclusão. Certifique-se de que nenhum serviço ativo depende dela antes de continuar.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={btnGhost} onClick={onClose}>Cancelar</button>
            <button style={{ ...btnPrimary, background: "var(--danger)" }} onClick={onConfirm}>
              Confirmar exclusão
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── ApiKeysPage ────────────────────────────────────────────────────────────
function ApiKeysPage() {
  const qc = useQueryClient();
  const { data: keys } = useQuery<ApiKey[]>({ queryKey: ["api-keys"], queryFn: () => api.get("/api-keys") });
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api-keys/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["api-keys"] }); setDeleteTarget(null); },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <h1 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Chaves API</h1>
        <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ Nova chave</button>
      </div>

      <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line)" }}>
              {["Nome", "Provider", "Chave", ""].map((h) => (
                <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys?.map((k) => (
              <tr key={k.id} style={{ borderTop: "1px solid var(--line)" }}>
                <td style={{ padding: "12px 16px", fontWeight: 500 }}>{k.name}</td>
                <td style={{ padding: "12px 16px", color: "var(--ink-secondary)", fontSize: 13 }}>{k.provider}</td>
                <td style={{ padding: "12px 16px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-secondary)" }}>{k.masked}</td>
                <td style={{ padding: "12px 16px", textAlign: "right" }}>
                  <button
                    onClick={() => setDeleteTarget(k)}
                    style={{ fontSize: 12, color: "var(--danger)", background: "none", border: "none", cursor: "pointer" }}
                  >
                    Deletar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateApiKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { qc.invalidateQueries({ queryKey: ["api-keys"] }); setShowCreate(false); }}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          keyName={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMut.mutate(deleteTarget.id)}
        />
      )}
    </div>
  );
}
